/**
 * @fileoverview CAP 4 — Gap detection (THE core product layer).
 *
 * Pure, deterministic rules engine that joins the case knowledge graph
 * (evidence / persons / warrants / surveillance / timeline + extracted phone
 * entities) and emits `Gap[]`. Each gap names WHAT was flagged, WHY, WHICH
 * source document triggered it (a `SourceRef`), the SUGGESTED next action, a
 * severity, and — for the time-sensitive Category C — an ISO `countdownTo`
 * deadline the UI renders as a live countdown.
 *
 * There is no single Foundry analog; the closest ones are Foundry's
 * "Object Monitoring" / "Checks" rule subsystem — a deterministic monitor that
 * surfaces conditions over an object set without ever auto-acting. IronCase
 * assists; the detective decides — no gap mutates anything.
 *
 * Categories:
 *   A  Missing Actions       (uninterviewed witness; unprocessed surveillance)
 *   B  Missing Documentation (unbooked evidence; unattached report exhibit)
 *   C  Time-Sensitive        (CalECPA expiry; surveillance overwrite; carrier
 *                             retention; lab request not initiated)
 *
 * Determinism: ids are `${ruleId}:${subjectId}` so a localStorage dismissal
 * keyed by id survives re-renders/reloads. `now` is injectable for tests.
 */

import type {
  Case,
  CaseTimelineEvent,
  EvidenceItem,
  Gap,
  GapCategory,
  GapSeverity,
  Person,
  SourceRef,
  SurveillanceClip,
  Warrant,
} from "./api";

// -- Time constants ----------------------------------------------------------

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** CalECPA delayed-notice flag window — surface 72h before the deadline. */
const CALECPA_NOTICE_WINDOW_MS = 72 * HOUR_MS;
/** Typical CCTV retention before overwrite (surveillance overwrite risk). */
const SURVEILLANCE_RETENTION_MS = 30 * DAY_MS;
/** Carrier records retention risk — warrant must issue within 7 days of open. */
const CARRIER_RETENTION_WINDOW_MS = 7 * DAY_MS;
/** Lab request should be initiated within 14 days for flagged forensic items. */
const LAB_REQUEST_WINDOW_MS = 14 * DAY_MS;

// -- Inputs ------------------------------------------------------------------

/**
 * The joined case data `detectGaps` runs over. Provided by `useCaseGaps`
 * (lib/hooks.ts); `timeline` is included because rule B-b inspects timeline
 * events for referenced-but-unlinked exhibits.
 */
export interface GapInputs {
  caseRecord: Case;
  evidence: EvidenceItem[];
  persons: Person[];
  warrants: Warrant[];
  surveillance: SurveillanceClip[];
  timeline: CaseTimelineEvent[];
  /** Extracted pertinent phones, from MOCK_ENTITIES kind === "phone". */
  phoneEntities: { phone: string; sourceRef: SourceRef }[];
  /** Injectable clock for tests/determinism; defaults to Date.now(). */
  now?: number;
}

// -- Severity by category ----------------------------------------------------

const SEVERITY_BY_CATEGORY: Record<GapCategory, GapSeverity> = {
  C: "urgent",
  A: "high",
  B: "medium",
};

// -- Helpers -----------------------------------------------------------------

/** Strip everything but digits so the two phone formats compare equal. */
function digitsOnly(value: string): string {
  return value.replace(/\D+/g, "");
}

/** Parse an ISO timestamp to epoch ms, or null when missing/unparseable. */
function toEpoch(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/** True when `text` contains any of `needles` (case-insensitive). */
function mentions(text: string | undefined, needles: string[]): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  return needles.some((n) => lower.includes(n));
}

/** First sourceRef on a record, or a synthesized one from the evidence id. */
function firstSourceRef(
  refs: SourceRef[] | undefined,
  fallback?: SourceRef,
): SourceRef | undefined {
  if (refs && refs.length > 0) return refs[0];
  return fallback;
}

// -- Rule engine -------------------------------------------------------------

/**
 * Compute every active gap over the joined case data. Pure + deterministic:
 * same inputs (with the same `now`) always yield the same `Gap[]`, sorted
 * Category C first (soonest deadline), then A, then B.
 */
export function detectGaps(input: GapInputs): Gap[] {
  const {
    caseRecord,
    evidence,
    persons,
    warrants,
    surveillance,
    timeline,
    phoneEntities,
  } = input;
  const now = input.now ?? Date.now();

  const gaps: Gap[] = [];

  // === Category A — Missing Actions =========================================

  // A-a Uninterviewed witness: a witness extracted into the case who has no
  // interview/statement document. "Documented" means either an explicit
  // interviewEvidenceId, the interviewed flag, OR an interview_transcript whose
  // title names the person — so a transcript linked only by title still counts.
  for (const person of persons) {
    if (person.role !== "witness") continue;
    const hasInterviewDoc = Boolean(person.interviewEvidenceId);
    const namedInTranscript = evidence.some(
      (e) =>
        e.kind === "interview_transcript" &&
        mentions(e.title, namesOf(person)),
    );
    if (person.interviewed || hasInterviewDoc || namedInTranscript) continue;

    gaps.push({
      id: `A-a-uninterviewed-witness:${person.id}`,
      category: "A",
      ruleId: "A-a-uninterviewed-witness",
      title: `Uninterviewed witness — ${person.fullName}`,
      why: `${person.fullName} was identified as a witness${
        person.reportsMentionedIn.length
          ? ` in ${person.reportsMentionedIn.join(", ")}`
          : ""
      } but no interview or statement document is on file.`,
      sourceRef: firstSourceRef(person.sourceRefs),
      suggestedAction: `Schedule and document an interview of ${person.fullName}, or record a documented outreach attempt.`,
      severity: SEVERITY_BY_CATEGORY.A,
    });
  }

  // A-b Unprocessed surveillance source: an identified camera (locationName
  // set) with no collection report AND/OR no collection date — i.e. it was
  // named in a canvass but never actually pulled.
  for (const clip of surveillance) {
    if (!clip.locationName) continue;
    const collected = Boolean(clip.dateCollected) && Boolean(clip.reportNumber);
    if (collected) continue;

    gaps.push({
      id: `A-b-unprocessed-surveillance:${clip.id}`,
      category: "A",
      ruleId: "A-b-unprocessed-surveillance",
      title: `Unprocessed surveillance source — ${clip.locationName}`,
      why: `${clip.locationName} was identified during the canvass but has ${
        clip.dateCollected ? "" : "no collection date"
      }${!clip.dateCollected && !clip.reportNumber ? " and " : ""}${
        clip.reportNumber ? "" : "no collection report"
      } — the footage has not been collected.`,
      sourceRef: firstSourceRef(clip.sourceRefs),
      suggestedAction: `Dispatch to collect footage from ${clip.locationName} and file a collection report before the retention window closes.`,
      severity: SEVERITY_BY_CATEGORY.A,
    });
  }

  // === Category B — Missing Documentation ===================================

  // B-a Unbooked evidence: a scene note / report describes an item as collected
  // or recovered (or it is flagged for forensic analysis) yet the evidence has
  // no item number — it was never booked into Property & Evidence.
  for (const item of evidence) {
    if (item.itemNumber) continue;
    const describedAsCollected =
      item.forensicAnalysisNeeded ||
      mentions(item.notes, ["collected", "recovered", "seized", "located"]);
    if (!describedAsCollected) continue;

    gaps.push({
      id: `B-a-unbooked-evidence:${item.id}`,
      category: "B",
      ruleId: "B-a-unbooked-evidence",
      title: `Unbooked evidence — ${item.title}`,
      why: `${item.title}${
        item.reportNumber ? ` (${item.reportNumber})` : ""
      } describes an item as collected at the scene, but no evidence item number has been assigned — it is not booked into Property & Evidence.`,
      sourceRef: firstSourceRef(item.sourceRefs),
      suggestedAction:
        "Book the item into Property & Evidence and assign an item number, or note in the report why it was not retained.",
      severity: SEVERITY_BY_CATEGORY.B,
    });
  }

  // B-b Unattached report exhibit: a timeline event references an attachment
  // ("Exhibit", "Attachment", "see photo") but no evidence file matches that
  // exhibit — the cited exhibit is not linked to the case file.
  for (const event of timeline) {
    const text = `${event.actions ?? ""} ${event.summary ?? ""}`;
    if (!mentions(text, ["exhibit", "attachment", "see photo", "attached as"]))
      continue;
    // A report whose every cited exhibit IS represented by a booked evidence row
    // is fine; we flag when the event references an attachment that has no
    // matching evidence row. ev-001/ev-002 use the base report number; the
    // exhibit-bearing supplements (S3) reference Exhibit D with no linked file.
    const exhibitLabel = extractExhibitLabel(text);
    const hasLinkedFile =
      event.evidenceIds.some((id) =>
        evidence.some(
          (e) => e.id === id && mentions(e.title, [exhibitLabel.toLowerCase()]),
        ),
      ) ||
      evidence.some((e) => mentions(e.title, [exhibitLabel.toLowerCase()]));
    if (hasLinkedFile) continue;

    gaps.push({
      id: `B-b-unattached-exhibit:${event.id}`,
      category: "B",
      ruleId: "B-b-unattached-exhibit",
      title: `Unattached report exhibit — ${exhibitLabel || "referenced attachment"}`,
      why: `${
        event.reportNumber ? `${event.reportNumber} ` : "A report "
      }references ${
        exhibitLabel || "an attachment"
      }, but no file matching that exhibit is linked to the case.`,
      sourceRef: firstSourceRef(
        undefined,
        event.evidenceIds[0]
          ? {
              evidenceId: event.evidenceIds[0],
              label: event.reportNumber
                ? `${event.reportNumber} — ${event.officer ?? "report"}`
                : "Case report",
            }
          : undefined,
      ),
      suggestedAction: `Locate and attach ${
        exhibitLabel || "the referenced exhibit"
      } to the case file, or correct the report's exhibit reference.`,
      severity: SEVERITY_BY_CATEGORY.B,
    });
  }

  // === Category C — Time-Sensitive (urgent, with countdowns) ================

  // C-a CalECPA expiration: a warrant with a delayed-notice (DON) expiration
  // within the next 72h (or already overdue). The DON expiry feeds the live
  // countdown. We surface overdue ones too (severity stays urgent).
  for (const warrant of warrants) {
    const expiry = toEpoch(warrant.donExpirationDate);
    if (expiry == null) continue;
    const msLeft = expiry - now;
    if (msLeft > CALECPA_NOTICE_WINDOW_MS) continue;

    const overdue = msLeft <= 0;
    const number = warrant.searchWarrantNumber ?? warrant.warrantNumber;
    gaps.push({
      id: `C-a-calecpa-expiration:${warrant.id}`,
      category: "C",
      ruleId: "C-a-calecpa-expiration",
      title: overdue
        ? `CalECPA notice OVERDUE — ${number}`
        : `CalECPA notice due — ${number}`,
      why: `${number}${
        warrant.serviceProvider ? ` (${warrant.serviceProvider})` : ""
      } has a CalECPA delayed-notice period that ${
        overdue ? "has already expired" : "expires within 72 hours"
      }. The target must be notified or an extension sought.`,
      sourceRef: firstSourceRef(warrant.sourceRefs),
      suggestedAction: overdue
        ? "Notify the target immediately or document the legal basis for the lapsed notice; consult the DA."
        : "Notify the target before the deadline or file a motion to extend the delayed-notice period.",
      severity: SEVERITY_BY_CATEGORY.C,
      countdownTo: warrant.donExpirationDate,
    });
  }

  // C-b Surveillance overwrite risk: a camera identified/collected whose
  // download has not been confirmed — the footage may be overwritten before it
  // is secured. Countdown = capture + typical 30-day retention.
  for (const clip of surveillance) {
    const identified = Boolean(clip.locationName) || Boolean(clip.dateCollected);
    if (!identified || clip.downloadConfirmed) continue;

    const captured = toEpoch(clip.capturedAt);
    const deadline =
      captured != null
        ? new Date(captured + SURVEILLANCE_RETENTION_MS).toISOString()
        : undefined;
    gaps.push({
      id: `C-b-surveillance-overwrite:${clip.id}`,
      category: "C",
      ruleId: "C-b-surveillance-overwrite",
      title: `Surveillance overwrite risk — ${clip.locationName ?? clip.title}`,
      why: `Footage from ${
        clip.locationName ?? clip.title
      } has been identified but its download has not been confirmed. Most systems overwrite within ~30 days of capture.`,
      sourceRef: firstSourceRef(clip.sourceRefs),
      suggestedAction:
        "Confirm the download/preservation hold with the camera owner and book the footage before the retention window closes.",
      severity: SEVERITY_BY_CATEGORY.C,
      countdownTo: deadline,
    });
  }

  // C-c Carrier data retention: an extracted pertinent phone with no covering
  // warrant, where the case has been open longer than the 7-day retention
  // window. Phone formats differ (entity "+1-702-555-0147" vs warrant E.164
  // "+17025550147"), so both sides are digit-normalized before matching.
  const coveredPhoneDigits = new Set(
    warrants
      .map((w) => w.pertinentPhone)
      .filter((p): p is string => Boolean(p))
      .map(digitsOnly),
  );
  const warrantTargetsDigits = warrants.map((w) => digitsOnly(w.target ?? ""));
  const caseOpened = toEpoch(caseRecord.createdAt);
  const caseAge = caseOpened != null ? now - caseOpened : 0;

  for (const entity of phoneEntities) {
    const phoneDigits = digitsOnly(entity.phone);
    if (!phoneDigits) continue;
    const covered =
      coveredPhoneDigits.has(phoneDigits) ||
      warrantTargetsDigits.some(
        (t) => phoneDigits.length >= 7 && t.includes(phoneDigits),
      );
    if (covered) continue;
    if (caseAge <= CARRIER_RETENTION_WINDOW_MS) continue;

    const deadline =
      caseOpened != null
        ? new Date(caseOpened + CARRIER_RETENTION_WINDOW_MS).toISOString()
        : undefined;
    gaps.push({
      id: `C-c-carrier-retention:${phoneDigits}`,
      category: "C",
      ruleId: "C-c-carrier-retention",
      title: `Carrier data retention — ${entity.phone}`,
      why: `${entity.phone} was identified as a pertinent number, but no warrant covers it and the case has been open more than 7 days. Carrier records (CDRs / CSLI) may age out of retention.`,
      sourceRef: entity.sourceRef,
      suggestedAction: `Draft and serve a carrier records warrant for ${entity.phone} before the data ages out.`,
      severity: SEVERITY_BY_CATEGORY.C,
      // The 7-day window is already elapsed; countdownTo points at the lapsed
      // deadline so the UI renders it as overdue rather than fabricating time.
      countdownTo: deadline,
    });
  }

  // C-d Lab request not initiated: evidence flagged for forensic analysis with
  // no lab submission, uploaded more than 14 days ago. Countdown = upload + 14d.
  for (const item of evidence) {
    if (!item.forensicAnalysisNeeded || item.sentToLab) continue;
    const uploaded = toEpoch(item.uploadedAt);
    if (uploaded == null) continue;
    if (now - uploaded <= LAB_REQUEST_WINDOW_MS) continue;

    const deadline = new Date(uploaded + LAB_REQUEST_WINDOW_MS).toISOString();
    gaps.push({
      id: `C-d-lab-not-initiated:${item.id}`,
      category: "C",
      ruleId: "C-d-lab-not-initiated",
      title: `Lab request not initiated — ${item.title}`,
      why: `${item.title}${
        item.analysisType ? ` (${item.analysisType})` : ""
      } is flagged for forensic analysis but has not been submitted to a lab, more than 14 days after collection.`,
      sourceRef: firstSourceRef(item.sourceRefs),
      suggestedAction: `Submit ${item.title} to the crime lab and record the lab request, or remove the forensic-analysis flag with a documented reason.`,
      severity: SEVERITY_BY_CATEGORY.C,
      countdownTo: deadline,
    });
  }

  return sortGaps(gaps);
}

// -- Sorting -----------------------------------------------------------------

const CATEGORY_ORDER: Record<GapCategory, number> = { C: 0, A: 1, B: 2 };

/**
 * Category C first (by soonest deadline), then A, then B. Within C a missing
 * deadline sorts last; ties break on id for stability.
 */
function sortGaps(gaps: Gap[]): Gap[] {
  return [...gaps].sort((a, b) => {
    const byCategory = CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category];
    if (byCategory !== 0) return byCategory;
    if (a.category === "C") {
      const at = toEpoch(a.countdownTo) ?? Number.POSITIVE_INFINITY;
      const bt = toEpoch(b.countdownTo) ?? Number.POSITIVE_INFINITY;
      if (at !== bt) return at - bt;
    }
    return a.id.localeCompare(b.id);
  });
}

// -- Name / exhibit extraction ----------------------------------------------

/** Lowercased name fragments used to match a person against a transcript title. */
function namesOf(person: Person): string[] {
  const full = person.fullName.toLowerCase();
  const parts = full.split(/\s+/).filter((p) => p.length > 2);
  const last = parts[parts.length - 1];
  return [full, ...(last ? [last] : [])];
}

/** Pull the exhibit label ("Exhibit B") out of a free-text action string. */
function extractExhibitLabel(text: string): string {
  const match = text.match(/exhibit\s+([A-Z0-9]+)/i);
  if (match) return `Exhibit ${match[1].toUpperCase()}`;
  if (/attachment/i.test(text)) return "attachment";
  if (/photo log/i.test(text)) return "photo log";
  return "referenced attachment";
}
